import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaSpinner } from 'react-icons/fa';
import { retryGiftVoucher, markGiftVoucherHandled } from '../../services/emailMonitorService';

// Takes the reply box's place for a gift voucher email: the sender is the voucher system, so there is
// nobody to answer, only an RCTI that was or was not made.

const money = (n) => (typeof n === 'number'
  ? n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })
  : '—');

const RctiLink = ({ gv }) => (gv.rctiId
  ? <Link to={`/rctis/${gv.rctiId}`} className="font-semibold text-jims-cyan hover:text-jims-cyan-dark underline">{gv.rctiNumber || 'the RCTI'}</Link>
  : <span className="font-semibold">{gv.rctiNumber || 'an RCTI'}</span>);

const GiftVoucherPanel = ({ email, onChanged, notify }) => {
  const gv = email.giftVoucher || {};
  const [busy, setBusy] = useState(null);

  const run = async (kind, action, successText) => {
    setBusy(kind);
    try {
      await action(email.id);
      notify?.({ type: 'success', text: successText });
    } catch (e) {
      notify?.({ type: 'error', text: e.message || 'Something went wrong.' });
    } finally {
      setBusy(null);
      // Refetch either way: a failed retry stores its new reason on the email.
      await onChanged?.();
    }
  };

  const details = [
    ['Franchisee', gv.franchiseeCode ? `${gv.franchiseeName || ''} (${gv.franchiseeCode})`.trim() : '—'],
    ['Amount (inc GST)', money(gv.amount)],
    ['Voucher', gv.voucherReference || '—'],
    ['Purchased', gv.voucherPurchaseDate || '—'],
  ];

  return (
    <div className="text-sm space-y-3">
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1">
        {details.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-gray-500">{label}</dt>
            <dd className="text-gray-900 font-medium break-words">{value}</dd>
          </div>
        ))}
      </dl>

      {gv.outcome === 'created' && (
        <p className="text-green-800">RCTI <RctiLink gv={gv} /> was created for this voucher.</p>
      )}
      {gv.outcome === 'duplicate' && (
        <p className="text-gray-700">
          {gv.rctiNumber
            ? <>This voucher already has RCTI <RctiLink gv={gv} />, so no second one was made.</>
            : 'Another copy of this email was already making the RCTI, so no second one was made.'}
        </p>
      )}
      {gv.outcome === 'handled_by_hand' && (
        <p className="text-gray-700">Marked as made by hand{gv.handledBy ? ` by ${gv.handledBy}` : ''}.</p>
      )}

      {gv.problems?.length > 0 && (
        <ul className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 list-disc list-inside">
          {gv.problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}

      {gv.outcome === 'needs_person' && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 space-y-2">
          <p className="text-amber-800"><b>No RCTI was made.</b> {gv.reason}</p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => run('retry', retryGiftVoucher, 'RCTI created.')}
              disabled={!!busy}
              className="px-3 py-1.5 font-medium text-white bg-jims-cyan rounded hover:bg-jims-cyan-dark disabled:opacity-50 inline-flex items-center gap-2"
            >
              {busy === 'retry' && <FaSpinner className="animate-spin" />} Try again
            </button>
            <button
              type="button"
              onClick={() => run('handled', markGiftVoucherHandled, 'Marked as made by hand.')}
              disabled={!!busy}
              className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {busy === 'handled' ? 'Saving…' : 'I made it by hand'}
            </button>
            <span className="text-xs text-amber-800">Fix the cause first (for example the franchisee code), then try again.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftVoucherPanel;
